import React, { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useCart } from '@/contexts/CartContext';
import { trackCartOpen } from '@/lib/analytics';
import { BUNDLE_PRODUCT_IDS } from '@/lib/product-catalog-data';

interface MiniCartContextType {
  isOpen: boolean;
  openMiniCart: () => void;
  closeMiniCart: () => void;
  setOpen: (open: boolean) => void;
  /** Single product that triggered the set-upgrade modal (null = closed). */
  upgradeFor: string | null;
  /** Close the upgrade modal and show the mini cart. */
  finishUpgrade: () => void;
}

const MiniCartContext = createContext<MiniCartContextType | undefined>(undefined);
const UPGRADE_SEEN_KEY = 'h2h_set_upgrade_shown';

const upgradeAlreadyShown = () => {
  try { return sessionStorage.getItem(UPGRADE_SEEN_KEY) === '1'; } catch { return false; }
};
const markUpgradeShown = () => {
  try { sessionStorage.setItem(UPGRADE_SEEN_KEY, '1'); } catch { /* ignore */ }
};

/**
 * Open/close state of the slide-out mini cart. Opens automatically whenever the
 * item count grows after hydration. When a single product is added while no set
 * is in the cart, the set-upgrade modal shows first (max. once per session).
 */
export const MiniCartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getItemCount, hydrated, items } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [upgradeFor, setUpgradeFor] = useState<string | null>(null);
  const count = getItemCount();
  const prev = useRef<number | null>(null);
  const prevItems = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!hydrated) return;
    const now = Object.fromEntries(items.map((i) => [i.id, i.quantity]));
    if (prev.current !== null && count > prev.current) {
      const grown = items.find((i) => i.quantity > (prevItems.current[i.id] ?? 0));
      const hasSet = items.some((i) => i.id === 'bundle');
      // Only when exactly one piece ("1 Stück") was added — not for 2/3-piece packages.
      const addedOne = grown && grown.quantity - (prevItems.current[grown.id] ?? 0) === 1;
      if (grown && addedOne && !hasSet && (BUNDLE_PRODUCT_IDS as string[]).includes(grown.id) && !upgradeAlreadyShown()) {
        markUpgradeShown();
        setUpgradeFor(grown.id);
      } else {
        setIsOpen(true);
        trackCartOpen(count);
      }
    }
    prev.current = count;
    prevItems.current = now;
  }, [count, hydrated, items]);

  const openMiniCart = useCallback(() => {
    setIsOpen(true);
    trackCartOpen(getItemCount());
  }, [getItemCount]);
  const closeMiniCart = useCallback(() => setIsOpen(false), []);
  const finishUpgrade = useCallback(() => {
    setUpgradeFor(null);
    setIsOpen(true);
    trackCartOpen(getItemCount());
  }, [getItemCount]);

  return (
    <MiniCartContext.Provider value={{ isOpen, openMiniCart, closeMiniCart, setOpen: setIsOpen, upgradeFor, finishUpgrade }}>
      {children}
    </MiniCartContext.Provider>
  );
};

export const useMiniCart = () => {
  const ctx = useContext(MiniCartContext);
  if (!ctx) throw new Error('useMiniCart must be used within MiniCartProvider');
  return ctx;
};
